import React, { useEffect, useState } from "react";
import {
    X, Wand2, ImageIcon, Layers, Aperture, Sun, Sparkles, ArrowLeft, Rewind, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ModalPortal } from "./ModalPortal";
import { ProductImage } from "./ProductImage";

/**
 * ProductImageEditor
 * -----------------------------------------------------------------------------
 * General-purpose product-image editor powered by Gemini Nano Banana.
 *
 * Every operation preserves the product's shape/label/text/logo/colours
 * exactly — only the requested aspect changes. Each edit is auto-backed-up
 * server-side, so admins can hop back to a previous version at any time
 * via the "History" panel.
 *
 * Supported operations map 1:1 to backend `/api/admin/products/{slug}/edit-image`:
 *   • listing_preset      — Carbon Labs Product Listing Standard (3:2 landscape)
 *   • background_replace  — Replace background with a described scene
 *   • landscape_convert   — Extend background to 3:2 landscape
 *   • isolate             — Transparent-background isolation
 *   • relight             — Adjust lighting only
 *   • custom              — Free-form instruction (product still preserved)
 */

const OPERATIONS = [
    {
        key: "listing_preset",
        label: "Apply Listing Preset",
        hint: "Carbon Labs Product Listing Standard — dark cinematic 3:2 landscape",
        icon: Sparkles,
        needs: [],
    },
    {
        key: "background_replace",
        label: "Replace Background",
        hint: "Swap background using your prompt. Product untouched.",
        icon: Layers,
        needs: ["background_prompt"],
    },
    {
        key: "landscape_convert",
        label: "Convert to Landscape",
        hint: "Extend background to a 3:2 landscape canvas.",
        icon: Aperture,
        needs: [],
    },
    {
        key: "isolate",
        label: "Isolate Product",
        hint: "Transparent-background PNG cutout.",
        icon: ImageIcon,
        needs: [],
    },
    {
        key: "relight",
        label: "Relight",
        hint: "Adjust lighting/shadows without touching the product.",
        icon: Sun,
        needs: ["style"],
    },
    {
        key: "custom",
        label: "Custom Instruction",
        hint: "Free-form edit. Product identity is still preserved.",
        icon: Wand2,
        needs: ["instruction"],
    },
];

export const ProductImageEditor = ({ product, token, onClose, onImageUpdated }) => {
    const headers = { "X-Admin-Token": token };
    const [op, setOp] = useState("listing_preset");
    const [prompt, setPrompt] = useState({ background_prompt: "", style: "", instruction: "" });
    const [running, setRunning] = useState(false);
    const [preview, setPreview] = useState(null); // { public_url, size, width, height, updated_at }
    const [backups, setBackups] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [busyIdx, setBusyIdx] = useState(null);

    const loadBackups = async () => {
        try {
            const { data } = await api.get(`/admin/products/${product.slug}/image-backups`, { headers });
            setBackups(data.backups || []);
        } catch { /* silent */ }
    };
    useEffect(() => { loadBackups(); /* eslint-disable-next-line */ }, [product.slug]);

    const meta = OPERATIONS.find((o) => o.key === op);

    const canRun = () => {
        if (!meta) return false;
        for (const field of meta.needs) {
            if (!(prompt[field] || "").trim()) return false;
        }
        return true;
    };

    const run = async () => {
        if (!canRun()) {
            toast.error("Fill in the required field for this operation.");
            return;
        }
        setRunning(true);
        setPreview(null);
        try {
            const body = { operation: op };
            for (const field of meta.needs) body[field] = (prompt[field] || "").trim();
            const { data } = await api.post(
                `/admin/products/${product.slug}/edit-image`,
                body,
                { headers, timeout: 90000 },
            );
            setPreview(data);
            toast.success(`Edit applied — previous version saved to history.`);
            onImageUpdated?.(data.public_url);
            loadBackups();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Image edit failed.");
        }
        setRunning(false);
    };

    const restore = async (idx) => {
        setBusyIdx(idx);
        try {
            const { data } = await api.post(
                `/admin/products/${product.slug}/restore-image`,
                { index: idx },
                { headers },
            );
            const url = data.public_url || product.image;
            setPreview({ public_url: url, ...data });
            toast.success("Restored from history.");
            onImageUpdated?.(url);
            loadBackups();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Restore failed.");
        }
        setBusyIdx(null);
    };

    // Cache-bust so <img> refreshes after edits/restores
    const currentUrl = preview?.public_url || product.image;
    const cacheBusted = currentUrl
        ? `${currentUrl}${currentUrl.includes("?") ? "&" : "?"}b=${Date.now()}`
        : currentUrl;

    return (
        <ModalPortal onClose={onClose}>
            <div
                className="glass-elevated rounded-2xl border border-[var(--gold)]/30 w-[min(96vw,1180px)] max-h-[92vh] overflow-hidden flex flex-col"
                data-testid="product-image-editor"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <Wand2 size={18} className="text-[var(--gold)]" />
                        <div>
                            <div className="text-sm font-display font-bold text-white">Product Image Editor</div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                {product.name} · {product.slug}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowHistory((s) => !s)}
                            className="btn-neon !py-1.5 !px-3 !text-[10px]"
                            data-testid="editor-toggle-history"
                        >
                            <Rewind size={12} /> History ({backups.length})
                        </button>
                        <button onClick={onClose} className="p-2 text-white/70 hover:text-white" aria-label="Close">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 flex-1 overflow-hidden">
                    {/* LEFT: preview */}
                    <div className="lg:col-span-3 p-5 border-b lg:border-b-0 lg:border-r border-white/5 overflow-y-auto">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2">
                            // Current asset · 3:2 landscape listing card
                        </div>
                        <div className="rounded-xl border border-[var(--gold)]/25 bg-gradient-to-b from-[#15181B] to-[#0B0D0F] overflow-hidden">
                            <ProductImage
                                product={{ ...product, image: cacheBusted }}
                                ratio="3 / 2"
                                loading="eager"
                            />
                        </div>
                        {preview && (
                            <div className="mt-3 text-[11px] text-[var(--text-secondary)]">
                                <span className="text-[var(--gold)] font-mono uppercase tracking-widest">// Latest edit</span>
                                <span className="ml-2">
                                    {preview.width}×{preview.height} · {(preview.size / 1024).toFixed(1)} KB
                                </span>
                            </div>
                        )}

                        {showHistory && (
                            <div className="mt-4 rounded-xl border border-white/5 p-3 bg-black/20" data-testid="editor-history-panel">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2">
                                    History · newest first
                                </div>
                                {backups.length === 0 ? (
                                    <div className="text-xs text-[var(--text-muted)] py-2">
                                        No previous versions yet. Each successful edit auto-saves the previous asset here.
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {backups.map((b, idx) => (
                                            <div key={idx} className="border border-white/5 rounded-lg overflow-hidden bg-black/30">
                                                <img
                                                    src={`data:${b.mime};base64,${b.data}`}
                                                    alt={`Backup ${idx + 1}`}
                                                    className="w-full aspect-[3/2] object-contain bg-[#0B0D0F]"
                                                    loading="lazy"
                                                />
                                                <div className="p-2 flex items-center justify-between gap-2">
                                                    <span className="text-[10px] font-mono text-[var(--text-muted)]">v{backups.length - idx}</span>
                                                    <button
                                                        onClick={() => restore(idx)}
                                                        disabled={busyIdx === idx}
                                                        className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold-hi)] hover:text-white"
                                                        data-testid={`editor-restore-${idx}`}
                                                    >
                                                        <ArrowLeft size={11} className="inline mr-0.5" />
                                                        {busyIdx === idx ? "…" : "Restore"}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* RIGHT: operations */}
                    <div className="lg:col-span-2 p-5 overflow-y-auto">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-2">
                            // Choose an operation
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                            {OPERATIONS.map((o) => {
                                const Icon = o.icon;
                                const active = op === o.key;
                                return (
                                    <button
                                        key={o.key}
                                        onClick={() => setOp(o.key)}
                                        className={`text-left rounded-xl border p-3 transition-all ${
                                            active
                                                ? "border-[var(--gold)] bg-[rgba(200,155,74,0.08)]"
                                                : "border-white/10 hover:border-white/25 bg-black/20"
                                        }`}
                                        data-testid={`editor-op-${o.key}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Icon size={14} className={active ? "text-[var(--gold)]" : "text-[var(--text-secondary)]"} />
                                            <span className={`text-sm font-display font-bold ${active ? "text-white" : "text-[var(--text-secondary)]"}`}>
                                                {o.label}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
                                            {o.hint}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Operation-specific inputs */}
                        <div className="mt-4 space-y-3">
                            {meta?.needs.includes("background_prompt") && (
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                        Describe the new background
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={prompt.background_prompt}
                                        onChange={(e) => setPrompt({ ...prompt, background_prompt: e.target.value })}
                                        placeholder="e.g. Polished black marble slab lit from the top-right, faint gold rim light, subtle warm bounce, deep obsidian backdrop, natural contact shadow."
                                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="editor-background-prompt"
                                    />
                                </div>
                            )}
                            {meta?.needs.includes("style") && (
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                        Lighting style
                                    </label>
                                    <input
                                        type="text"
                                        value={prompt.style}
                                        onChange={(e) => setPrompt({ ...prompt, style: e.target.value })}
                                        placeholder="e.g. warm three-point studio with soft key from upper-left"
                                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="editor-relight-style"
                                    />
                                </div>
                            )}
                            {meta?.needs.includes("instruction") && (
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                        Instruction (product identity is still preserved)
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={prompt.instruction}
                                        onChange={(e) => setPrompt({ ...prompt, instruction: e.target.value })}
                                        placeholder="e.g. Add a faint reflective surface under the product with a warm gold rim light on the right."
                                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="editor-custom-instruction"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex items-start gap-2 text-[10px] text-[var(--text-muted)] leading-relaxed">
                            <Sparkles size={12} className="text-[var(--gold)] mt-0.5 shrink-0" />
                            <span>
                                Product shape, label artwork, text, typography, logo and colours are preserved on every operation.
                                Only the aspect you request changes. The previous asset is auto-saved to History.
                            </span>
                        </div>

                        <div className="mt-4 sticky bottom-0 bg-gradient-to-t from-[#0B0D0F] via-[#0B0D0F] to-transparent pt-4">
                            <button
                                onClick={run}
                                disabled={running || !canRun()}
                                className="btn-solid w-full"
                                data-testid="editor-run"
                            >
                                {running ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" /> Editing… ~10-30s
                                    </>
                                ) : (
                                    <>
                                        <Wand2 size={14} /> Run edit
                                    </>
                                )}
                            </button>
                            <div className="mt-2 text-center text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Powered by Gemini Nano Banana
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default ProductImageEditor;
